import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useRevalidator } from 'react-router';

import SideArea from 'src/components/ui/SideArea';
import RightDrawer from 'src/components/ui/RightDrawer';
import DefaultFormCAreaName from 'src/components/ui/DefaultForm/connectors/AreaName';
import DefaultFormCWorth from 'src/components/ui/DefaultForm/connectors/Worth';
import TableValuation from 'src/components/ui/TableValuation';
import { mapCompanyToSideArea } from 'src/utils/mappers/companySideArea/index.mapper';
import {
	buildWorthFormOptions,
	hasWorthFormChanges,
	mapFormValuesToWorthInput,
	mapWorthToFormValues,
} from 'src/utils/mappers/worth/form.mapper';
import { mapEventSelectionsToTableValuation } from 'src/utils/mappers/worth/eventSelections.mapper';
import {
	hasSalesTargetFormChanges,
	mapSalesTargetFormToInput,
} from 'src/utils/mappers/companySalesTarget.mapper';
import { updateCompany } from 'src/services/updateCompany';
import { updateCompanyAndWorth } from 'src/services/updateCompanyAndWorth';
import { updateWorth } from 'src/services/updateWorth';

export default function CompanySideArea({ company }) {
	const revalidator = useRevalidator();
	const [isEditNameOpen, setIsEditNameOpen] = useState(false);
	const [isEditWorthOpen, setIsEditWorthOpen] = useState(false);
	const [serverError, setServerError] = useState(null);

	const handleOpenEditName = useCallback(() => {
		setServerError(null);
		setIsEditNameOpen(true);
	}, []);

	const handleCloseEditName = useCallback(() => {
		setIsEditNameOpen(false);
		setServerError(null);
	}, []);

	const handleOpenEditWorth = useCallback(() => {
		setServerError(null);
		setIsEditWorthOpen(true);
	}, []);

	const handleCloseEditWorth = useCallback(() => {
		setIsEditWorthOpen(false);
		setServerError(null);
	}, []);

	const handleSubmitName = useCallback(
		async ({ name }) => {
			try {
				setServerError(null);
				await updateCompany(company.id, { name });
				handleCloseEditName();
				await revalidator.revalidate();
			} catch (error) {
				setServerError(error?.message ? error : 'Не удалось изменить название');
			}
		},
		[company?.id, handleCloseEditName, revalidator],
	);

	const handleSubmitWorth = useCallback(
		async (values) => {
			try {
				setServerError(null);

				const worthChanged = hasWorthFormChanges(
					values,
					company?.worth,
				);
				const salesTargetChanged = hasSalesTargetFormChanges(
					values,
					company,
				);

				if (worthChanged && salesTargetChanged) {
					await updateCompanyAndWorth(company.id, {
						companyInput: mapSalesTargetFormToInput(values),
						worthInput: mapFormValuesToWorthInput(values),
					});
				} else if (worthChanged) {
					await updateWorth(
						company.id,
						mapFormValuesToWorthInput(values),
					);
				} else if (salesTargetChanged) {
					await updateCompany(
						company.id,
						mapSalesTargetFormToInput(values),
					);
				}

				handleCloseEditWorth();
				await revalidator.revalidate();
			} catch (error) {
				setServerError(
					error?.message ? error : 'Не удалось изменить ценность',
				);
			}
		},
		[company, handleCloseEditWorth, revalidator],
	);

	const worthInitialData = useMemo(
		() => mapWorthToFormValues(company?.worth, company),
		[company],
	);

	const worthFormOptions = useMemo(
		() => buildWorthFormOptions(company?.worth),
		[company?.worth],
	);

	const worthValuationTable = useMemo(
		() => mapEventSelectionsToTableValuation(company?.eventSelections),
		[company?.eventSelections],
	);

	const canEditCompanyFields = Boolean(company && !company.isGroup);

	const sideAreaData = useMemo(() => {
		const data = mapCompanyToSideArea(company, {
			onEditWorth: canEditCompanyFields
				? handleOpenEditWorth
				: undefined,
		});

		if (company?.isGroup) {
			data.onEditLabel = handleOpenEditName;
		}

		return data;
	}, [company, canEditCompanyFields, handleOpenEditName, handleOpenEditWorth]);

	return (
		<>
			<SideArea {...sideAreaData} />
			{company?.isGroup && (
				<RightDrawer
					open={isEditNameOpen}
					onCloseDrawer={handleCloseEditName}
				>
					{isEditNameOpen && (
						<DefaultFormCAreaName
							fullHeight
							initialData={{ name: company.name }}
							serverError={serverError}
							onCancel={handleCloseEditName}
							onSubmit={handleSubmitName}
						/>
					)}
				</RightDrawer>
			)}
			{canEditCompanyFields && (
				<RightDrawer
					open={isEditWorthOpen}
					onCloseDrawer={handleCloseEditWorth}
				>
					{isEditWorthOpen && (
						<DefaultFormCWorth
							fullHeight
							withSalesTarget
							acceptLabel="Изменить"
							disableAcceptUntilDirty
							initialData={worthInitialData}
							options={worthFormOptions}
							pre={
								worthValuationTable ? (
									<TableValuation {...worthValuationTable} />
								) : undefined
							}
							serverError={serverError}
							onCancel={handleCloseEditWorth}
							onSubmit={handleSubmitWorth}
						/>
					)}
				</RightDrawer>
			)}
		</>
	);
}

CompanySideArea.propTypes = {
	company: PropTypes.object,
};
